import React from 'react';
import { Task } from '@models/task.model';
import { getTaskStatusDisplay, translateTaskDisplayValue } from '@utils/taskDisplay';
import { Badge } from '@components/atoms/Badge';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import Alert from '@components/atoms/Alert';
import { formatForDisplay } from '@utils/formatDate';
import { ButtonLink } from '@components/atoms/ButtonLink';
import { cn } from '@utils/cn';
import RichTextView from '@components/atoms/RichTextView';
import { useAppSelector } from '@redux/hooks';
import { RootState } from '@redux/store';
import { isExternalViewerRole } from '@constants/roleHierarchy';
import { useTranslation } from '@hooks/useTranslation';

interface TaskDetailHeaderProps {
  task: Task | null;
  progressValue: number;
}

export const TaskDetailHeader: React.FC<TaskDetailHeaderProps> = ({
  task,
  progressValue,
}) => {
  // Sprint 25, story-127 sweep item #9 (the AC's own named leak). The `task?.budget` guard
  // below already renders nothing once the server nulls the field for REVIEWER/CLIENT
  // (story-127's @ResolveField redaction) — this only needs to additionally hide "View in
  // Project", which points at /schedule, a page neither external-viewer role can open.
  const { role } = useAppSelector((state: RootState) => state.user);
  const isExternalViewer = isExternalViewerRole(role);
  // Get the visual display status (includes calculated states like UPCOMING, BEHIND_SCHEDULE).
  // Story-071 (sprint 14): render with this palette color directly, rather than mapping
  // displayValue to one of Badge's six generic variants — that mapping lost the canonical
  // per-status color (both approvals rendered the same yellow, for example).
  // Sprint 28 change request (global sweep, defect fix). `displayInfo.label` used to be raw
  // English text — see `@utils/taskDisplay.ts`'s `TaskDisplayInfo.label` comment.
  const tTaskDetail = useTranslation('taskDetail');
  const displayInfo = getTaskStatusDisplay(task);
  const statusLabel = translateTaskDisplayValue(displayInfo.displayValue, tTaskDetail);

  return (
    <div className="space-y-6">
      {/* Header Row - Status and Progress */}
      <div className="flex items-center justify-between">
        <div className="flex items-end justify-between w-full">
          <div>
            <p className="text-sm font-medium text-gray-900 dark:text-white">
              Task name:
            </p>
            <div className="flex gap-2 items-center mt-1">
              <p className="text-lg font-medium text-gray-900 dark:text-white">
                {task?.name}
              </p>
              <Badge
                color={displayInfo.color}
                size="md"
                className={cn('flex items-center gap-2')}>
                {statusLabel}
              </Badge>
            </div>
          </div>
          {task?.taskType && (
            <Badge className="text-sm font-medium text-gray-900 dark:text-white">
              {task.taskType}
            </Badge>
          )}
        </div>
      </div>

      {/* Task Title & Description */}
      {task?.description && (
        <Alert className="space-y-2">
          <RichTextView
            value={task.description}
            className="text-gray-900 dark:text-white leading-relaxed text-base"
          />
        </Alert>
      )}

      {/* Metadata Row */}
      <div
        className={cn(
          'flex flex-wrap gap-4 items-center',
          task?.budget ? 'justify-between' : 'justify-end',
        )}>
        {task?.budget && (
          <div className="flex items-center gap-2">
            <AttachMoneyIcon className="w-4 h-4 text-gray-400" />
            <span className="text-sm font-medium text-gray-900 dark:text-white">
              Budget: ${task.budget.toLocaleString()}
            </span>
          </div>
        )}
        {!isExternalViewer && (
          <div className="flex items-center gap-2">
            <ButtonLink
              color="primary"
              href={`/schedule/${task?.projectId}/${task?.taskId}`}>
              View in Project
            </ButtonLink>
          </div>
        )}
      </div>

      {/* Time Information */}
      {(task?.startTime || task?.endTime) && (
        <div className="flex gap-6">
          {task?.startTime && (
            <div className="space-y-1">
              <span className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                Start
              </span>
              <div className="text-sm font-medium text-gray-900 dark:text-white">
                {formatForDisplay(task.startTime)}
              </div>
            </div>
          )}
          {task?.endTime && (
            <div className="space-y-1">
              <span className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                End
              </span>
              <div className="text-sm font-medium text-gray-900 dark:text-white">
                {formatForDisplay(task.endTime)}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TaskDetailHeader;
